<template>
    <div class="email-element-rule">
        <resource-select
            :value="selectedEmail"
            :options="emailOptions"
            :loading="loadingResources"
            field="emailId"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('emailElementRule.Select Email')"
            @input="onEmailSelect">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="selectedEmail">
            <pendo-multiselect
                v-if="selectedEmail"
                :value="selectedEmailElement"
                full-width
                :append-to-body="false"
                :disabled="readOnly"
                :extra-label-width="90"
                :allow-empty="false"
                :options="emailElementOptions"
                :loading="loading"
                label-key="name"
                value-key="emailElementId"
                :placeholder="$t('emailElementRule.Select Email Element')"
                @select="onElementSelect">
                <template #noData>
                    <div class="no-data-message">
                        {{ $t('emailElementRule.No email elements found. Please select a different email.') }}
                    </div>
                </template>
                <template #selectedLabel="{ option }">
                    <pendo-icon-option :option="option" />
                </template>
                <template #option="{ option }">
                    <pendo-icon-option :option="option" />
                </template>
            </pendo-multiselect>
            <template v-if="selectedEmailElement">
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Condition') }}</strong>
                    <div class="flex-break" />
                </template>
                <fact-select
                    :options="factOptions"
                    :value="factValue"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="$emit('input', $event)" />
                <template v-if="rule.schema === 'emailElement'">
                    <template v-if="!isLocaleEnglish">
                        <div class="flex-break" />
                        <strong>{{ $t('Criteria') }}</strong>
                        <div class="flex-break" />
                    </template>
                    <simple-select
                        :options="clickedOptions"
                        :value="clickedValue"
                        preselect-first
                        field="clicked"
                        :disabled="readOnly"
                        :check-valid-rule="checkValidRule"
                        @input="$emit('input', $event)" />
                    <component
                        :is="valueComponent.component"
                        v-if="valueComponent"
                        v-bind="valueComponent.props"
                        :read-only="readOnly"
                        :check-valid-rule="checkValidRule"
                        @input="$emit('input', $event)" />
                </template>
                <template v-if="rule.schema === 'aggregation'">
                    <aggregation-rule
                        :rule="rule"
                        :read-only="readOnly"
                        :check-valid-rule="checkValidRule"
                        :is-locale-english="isLocaleEnglish"
                        @input="$emit('input', $event)" />
                </template>
            </template>
        </template>
    </div>
</template>

<script>
import { LOADING_STATES } from '@/constants';
import EmailElementsTableAggregation from '@/emails/aggregations/email-elements-table.aggregation';
import { fitEmailStatus, getUiElementTypeIcon, isIntegratedEmail, sanitizeUrl } from '@/emails/helpers';
import { getContent } from '@/guides/helpers/common.util';
import AggregationRule from '@/segments/components/segment-editor-vue/aggregation-rule.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import {
    formatFactOptions,
    getFactOptions,
    getFactValue,
    getNoResourcesFoundText,
    getNumberConfig,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { connectAggregationToState, withComponent, withStore } from '@pendo/agg-connect';
import { PendoIconOption, PendoMultiselect } from '@pendo/components';
import { getCurrentElementData } from '@pendo/one-ui-shared';
import { BuildingBlock } from '@pendo/services/BuildingBlocks';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import { mapActions, mapState } from 'vuex';

const { LOADING, RESOLVED, REJECTED, EMPTY } = LOADING_STATES;

export default {
    name: 'EmailElementRule',
    components: {
        AggregationRule,
        FactSelect,
        ResourceSelect,
        PendoIconOption,
        PendoMultiselect,
        SingleDate,
        NumberInput,
        RelativeDateRange,
        SimpleSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            elementsActivityAggStatus: null,
            email: {},
            emailElements: [],
            currentElementData: [],
            currentIds: [],
            elementsActivityAgg$: null,
        };
    },
    computed: {
        ...mapState({
            associatedGuide: (state) => state.guides.guide,
        }),
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp, resourceType: 'email' });
        },
        emailOptions () {
            if (!this.resources || !Array.isArray(this.resources)) {
                return [];
            }

            // filter out integrated emails since we don't support email elements for them
            const emailOptions = this.resources
                .filter((email) => !isIntegratedEmail(email))
                .map(({ id, name, status, guideId }) => ({
                    emailId: id,
                    name,
                    state: fitEmailStatus(status),
                    guideId,
                }));

            if (
                this.rule.emailId &&
                this.rule.emailElementId &&
                !emailOptions.find((email) => email.emailId === this.rule.emailId)
            ) {
                emailOptions.push({
                    emailId: this.rule.emailId,
                    name: this.$t('emailElementRule.Deleted Email ({emailId})', { emailId: this.rule.emailId }),
                    state: 'deleted',
                });
            }

            return emailOptions;
        },
        emailElementOptions () {
            return this.emailElements;
        },
        selectedEmail () {
            return this.emailOptions.find((email) => email.emailId === this.rule.emailId);
        },
        selectedEmailElement () {
            return this.emailElements.find((emailElement) => emailElement.emailElementId === this.rule.emailElementId);
        },
        factOptions () {
            const options = getFactOptions({
                id: this.rule.id,
                onlyAllowGuideCompatibleRules: this.config.onlyAllowGuideCompatibleRules,
            });

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        loading () {
            return this.elementsActivityAggStatus === LOADING;
        },
        clickedOptions () {
            const options = [
                { clicked: 'ever', name: this.$t('emailElementRule.ever') },
                { clicked: 'since', name: this.$t('emailElementRule.since') },
                { clicked: 'withinLast', name: this.$t('emailElementRule.within last') },
            ];

            if (this.rule.fact === 'clicked') {
                options.push(
                    { clicked: 'atleast', name: this.$t('emailElementRule.at least') },
                    { clicked: 'atmost', name: this.$t('emailElementRule.at most') },
                );
            }

            return options;
        },
        clickedValue () {
            return this.clickedOptions.find((option) => option.clicked === this.rule.clicked);
        },
        valueComponent () {
            switch (this.rule.clicked) {
                case 'withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'since':
                    return getSingleDateConfig(this.rule, 'value');
                case 'atmost':
                case 'atleast': {
                    const component = getNumberConfig(this.rule, 'value');
                    component.props.labels = { suffix: this.$t('emailElementRule.times') };

                    return component;
                }
                default:
                    return false;
            }
        },
    },
    watch: {
        selectedEmail: {
            immediate: true,
            async handler (newEmail) {
                if (!newEmail) return;

                this.email = this.resources.find((email) => email.id === newEmail.emailId);
                if (!this.email) {
                    this.email = { id: newEmail.emailId };
                    this.currentIds = [];

                    return;
                }

                await this.loadGuide({ id: newEmail.guideId, setActive: true });

                if (this.associatedGuide) {
                    await this.extractElementsFromGuide();
                }
            },
        },
    },
    created () {
        this.createEmailElementActivityAgg$();
    },
    unmounted () {
        if (this.elementsActivityAgg$) {
            this.elementsActivityAgg$.unsubscribe();
        }
    },
    methods: {
        ...mapActions({
            loadGuide: 'guides/loadOne',
        }),
        async extractElementsFromGuide () {
            if (!this.associatedGuide) return;

            try {
                const guideWithContent = await getContent(this.associatedGuide);

                this.currentElementData = guideWithContent.steps.reduce((acc, step) => {
                    const domElements = getCurrentElementData(JSON.parse(step.dom));
                    const buildingBlocks = JSON.parse(step?.buildingBlocks || '[]');

                    const stepElementData = domElements.map(
                        ({ uiElementId: id, uiElementText: name, uiElementActions, uiElementType: type }) => {
                            let link = '';
                            if (uiElementActions?.length > 0) {
                                switch (type) {
                                    case 'Button':
                                    case 'Image':
                                        link =
                                            uiElementActions?.at(0)?.parameters?.find((param) => param.name === 'url')
                                                ?.value || '';
                                        break;
                                    case 'Link':
                                        link =
                                            BuildingBlock.findBlockByDomId(buildingBlocks, id)?.properties.find(
                                                (prop) => prop.name === 'href',
                                            )?.value || '';
                                        break;
                                }
                            }

                            return {
                                id,
                                name,
                                type,
                                link: sanitizeUrl(link),
                                guideStepId: step.id,
                            };
                        },
                    );

                    acc.push(...stepElementData);

                    return acc;
                }, []);

                this.currentIds = this.currentElementData.map((element) => element.id);
            } catch {
                this.currentElementData = [];
                this.currentIds = [];
            }
        },
        createEmailElementActivityAgg$ () {
            this.elementsActivityAggStatus = LOADING;
            const sourcesOfState = {
                component: this,
                store: this.$store,
            };
            const elementsStream$ = connectAggregationToState(
                EmailElementsTableAggregation,
                withStore({
                    blacklist: (state, getters) => getters['globalFilters/segmentBlacklist'],
                    includeAnonymous: (state, getters) => getters['globalFilters/segmentIncludeAnonymous'],
                    timeSeries: (state, getters) => getters['globalFilters/timeSeriesSpec'],
                }),
                withComponent({
                    emailId: 'email.id',
                    currentIds: 'currentIds',
                }),
            )(sourcesOfState);

            this.elementsActivityAgg$ = elementsStream$.subscribe(({ status, value }) => {
                switch (status) {
                    case REJECTED:
                    case LOADING: {
                        this.elementsActivityAggStatus = status;
                        this.emailElements = [];
                        break;
                    }
                    case RESOLVED: {
                        const elementsActivity = this.processEmailElementsAggDropdown(value, this.currentElementData);

                        if (
                            this.rule.emailElementId &&
                            !elementsActivity.find((element) => element.emailElementId === this.rule.emailElementId)
                        ) {
                            elementsActivity.push({
                                emailElementId: this.rule.emailElementId,
                                name: this.$t('emailElementRule.Deleted Element ({emailElementId})', {
                                    emailElementId: this.rule.emailElementId,
                                }),
                            });
                        }

                        this.elementsActivityAggStatus = elementsActivity.length ? RESOLVED : EMPTY;
                        this.emailElements = elementsActivity;
                        break;
                    }
                }
            });
        },
        processEmailElementsAggDropdown (aggregationData, elementData) {
            const mergedData = merge(keyBy(aggregationData, 'id'), keyBy(elementData, 'id'));

            return Object.values(mergedData).map((element) => {
                return {
                    emailElementId: element.id,
                    name: element.name || element.type || element.id,
                    type: element.type,
                    icon: { type: getUiElementTypeIcon(element.type) },
                };
            });
        },
        onEmailSelect (email) {
            const field = ['emailId', 'emailElementId'];
            const value = { emailId: email.value.emailId, emailElementId: null };

            this.$emit('input', { field, value });
        },
        onElementSelect (element) {
            this.$emit('input', { field: 'emailElementId', value: element.emailElementId });
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
